import { Comparison } from './../comparisons/comparison.types';
import {
	MULTI_SHARE_CLOSE_DIALOG,
	MULTI_SHARE_DESELECT_COMPARISON,
	MULTI_SHARE_DESELECT_GROUP,
	MULTI_SHARE_DESELECT_GROUP_ALL,
	MULTI_SHARE_DESELECT_PROJECT,
	MULTI_SHARE_DESELECT_STUDENT,
	MULTI_SHARE_LOAD_GROUPS,
	MULTI_SHARE_OPEN_DIALOG,
	MULTI_SHARE_SELECT_COMPARISON,
	MULTI_SHARE_SELECT_GROUP,
	MULTI_SHARE_SELECT_GROUP_ALL,
	MULTI_SHARE_SELECT_PROJECT,
	MULTI_SHARE_SELECT_STUDENT
} from './multi-share.actions';
import produce from 'immer';
import { remove } from 'ramda';
import { Project } from '../projects/projects.types';
import { IMultiShareState, MultiShareActions } from './multi-share.types';
import { Group } from '../user/user.types';

const multiShareInitialState: IMultiShareState = {
	dialogOpen: false,
	groups: [],
	selectedProjects: [],
	selectedComparisons: [],
	selectedGroups: []
};

export function multiShareReducer(
	state: IMultiShareState = multiShareInitialState,
	action: MultiShareActions
): IMultiShareState {
	switch (action.type) {
		case MULTI_SHARE_OPEN_DIALOG:
			return {
				...state,
				dialogOpen: true
			};
		case MULTI_SHARE_CLOSE_DIALOG:
			return multiShareInitialState;
		case MULTI_SHARE_SELECT_PROJECT:
			return selectProject(state, action.payload as Project);
		case MULTI_SHARE_DESELECT_PROJECT:
			return deselectProject(state, action.payload as Project);
		case MULTI_SHARE_SELECT_COMPARISON:
			return selectComparison(state, action.payload as Comparison);
		case MULTI_SHARE_DESELECT_COMPARISON:
			return deselectComparison(state, action.payload as Comparison);
		case MULTI_SHARE_SELECT_GROUP_ALL:
			return selectAllGroups(state, action.payload as Group[]);
		case MULTI_SHARE_DESELECT_GROUP_ALL:
			return deselectAllGroups(state);
		case MULTI_SHARE_SELECT_GROUP:
			return selectGroup(state, action.payload as Group);
		case MULTI_SHARE_DESELECT_GROUP:
			return deselectGroup(state, action.payload as Group);
		case MULTI_SHARE_SELECT_STUDENT:
			return selectStudent(state, action.payload as Group);
		case MULTI_SHARE_DESELECT_STUDENT:
			return deselectStudent(state, action.payload as Group);
		case MULTI_SHARE_LOAD_GROUPS:
			return loadGroups(state, action.payload as Group[]);
		default:
			return state;
	}
}

function selectStudent(
	state: IMultiShareState,
	group: Group
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		const groupIndex: number = state.selectedGroups.findIndex(
			(x) => x.id === group.id
		);
		groupIndex === -1
			? draftState.selectedGroups.push(group)
			: draftState.selectedGroups[groupIndex].students.push(
					group.students[0]
			  );
	});
}

function deselectStudent(
	state: IMultiShareState,
	group: Group
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		const groupIndex: number = state.selectedGroups.findIndex(
			(x) => x.id === group.id
		);
		if (groupIndex !== -1) {
			const studentIndex: number = state.selectedGroups[
				groupIndex
			].students.findIndex((x) => x.id === group.students[0].id);

			if (studentIndex !== -1) {
				draftState.selectedGroups[groupIndex].students.length === 1
					? (draftState.selectedGroups = remove(
							groupIndex,
							1,
							draftState.selectedGroups
					  ))
					: (draftState.selectedGroups[groupIndex].students = remove(
							studentIndex,
							1,
							draftState.selectedGroups[groupIndex].students
					  ));
			}
		}
	});
}

function selectProject(
	state: IMultiShareState,
	project: Project
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		draftState.selectedProjects.push(project);
	});
}

function deselectProject(
	state: IMultiShareState,
	project: Project
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		const index: number = draftState.selectedProjects.findIndex(
			(selectedProject: Project) => selectedProject.id === project.id
		);
		if (index !== -1) {
			draftState.selectedProjects = remove(
				index,
				1,
				draftState.selectedProjects
			);
		}
	});
}

function selectComparison(
	state: IMultiShareState,
	comparison: Comparison
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		draftState.selectedComparisons.push(comparison);
	});
}

function deselectComparison(
	state: IMultiShareState,
	comparison: Comparison
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		const index: number = draftState.selectedComparisons.findIndex(
			(selectedComparison: Comparison) =>
				selectedComparison.id === comparison.id
		);
		if (index !== -1) {
			draftState.selectedComparisons = remove(
				index,
				1,
				draftState.selectedComparisons
			);
		}
	});
}

function selectAllGroups(
	state: IMultiShareState,
	groups: Group[]
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		draftState.selectedGroups = groups;
	});
}

function deselectAllGroups(state: IMultiShareState): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		draftState.selectedGroups = [];
	});
}

function selectGroup(state: IMultiShareState, group: Group): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		draftState.selectedGroups.push(group);
	});
}

function deselectGroup(
	state: IMultiShareState,
	group: Group
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		const index: number = draftState.selectedGroups.findIndex(
			(selectedGroup: Group) => selectedGroup.id === group.id
		);
		if (index !== -1) {
			draftState.selectedGroups = remove(
				index,
				1,
				draftState.selectedGroups
			);
		}
	});
}

function loadGroups(
	state: IMultiShareState,
	groups: Group[]
): IMultiShareState {
	return produce(state, (draftState: IMultiShareState) => {
		draftState.groups = groups;
	});
}
